import { useMemo } from 'react';
import { companyGqlMapper } from '@/hooks/useInfiniteCompanyScreenersQuery/utils';
import { useGridCompanies } from '@/hooks/useGridCompanies';
import { useFlag } from '@/features/unleash';
import { toCurrency } from '@/utilities/formatting';
import { getIsRestricted, getRestrictionReason } from './utils';
import { checkIsPlanRestriction } from '@/hooks/useInfiniteCompanyScreenersQuery/utils';
import { useInfiniteScreenersQuery } from './useInfiniteScreenersQuery';
import { useUserAccess } from '@/hooks/useUserAccess';

type ScreenerQueryParams = {
  gridViewId?: number;
  rules?: string;
  pageSize: number;
  returnRecentCompaniesOnly?: boolean;
  isRulesDirty: boolean;
};

export const useScreenerQuery = ({
  gridViewId,
  rules,
  pageSize,
  returnRecentCompaniesOnly,
  isRulesDirty,
}: ScreenerQueryParams) => {
  const featureFlag = useFlag({ name: 'mono-screener-gql' });
  const isFeatureEnabled =
    featureFlag.flagsReady &&
    featureFlag.enabled &&
    featureFlag.variant.name === 'variant';

  const {
    data: dataGQL,
    fetchNextPage: fetchNextPageGQL,
    hasNextPage: hasNextPageGQL,
    isFetching: isFetchingCompaniesGQL,
    isFetched: isFetchedGQL,
    isError: isErrorGQL,
  } = useInfiniteScreenersQuery({
    isFeatureEnabled,
    gridViewId,
    rules,
    pageSize,
    returnRecentCompaniesOnly,
    isRulesDirty,
  });

  const enhancedFetchNextPageGQL = useMemo(() => {
    const shouldFetchNextPage = !isErrorGQL && !isFetchingCompaniesGQL;
    return shouldFetchNextPage ? fetchNextPageGQL : () => {};
  }, [fetchNextPageGQL, isErrorGQL, isFetchingCompaniesGQL]);

  const {
    data: dataRestApi,
    fetchNextPage: fetchNextPageRestApi,
    hasNextPage: hasNextPageRestApi,
    isFetching: isFetchingCompaniesRestApi,
    isFetched: isFetchedRestApi,
  } = useGridCompanies({
    enabled: !isFeatureEnabled,
    rules: rules,
    pageSize,
    isInfinite: true,
    include: ['grid', 'score', 'info'],
  });

  const totalCompaniesRestApi = toCurrency(
    dataRestApi.pages[0]?.meta.totalRecords || 0,
    0
  ).split('$')[1];
  const companiesRestApi = useMemo(
    () => dataRestApi.pages.map((page) => page.companies).flat(),
    [dataRestApi]
  );

  const firstPage = dataGQL?.pages[0];
  const totalRecords = firstPage?.totalHits ?? 0;
  const totalCompaniesGQL = toCurrency(totalRecords || 0, 0).split('$')[1];

  const companiesGQL = useMemo(() => {
    const flattenCompanies =
      dataGQL?.pages
        .map((page) => page.companies)
        .flat()
        .filter(Boolean) || [];
    return flattenCompanies.map(companyGqlMapper);
  }, [dataGQL]);

  const searchRestrictions = firstPage?.meta?.searchRestrictions;
  const systemRestrictions = firstPage?.meta?.systemRestrictions;

  const isRestricted = getIsRestricted(searchRestrictions, systemRestrictions);
  const { data: uac } = useUserAccess();

  const restrictedReason = useMemo(() => {
    const numberOfViewableScreeners = uac?.numberOfViewableScreeners;
    const maxResultCount =
      systemRestrictions?.maxResultCountForRecentCompanyFeature;
    return getRestrictionReason({
      searchRestrictions,
      systemRestrictions,
      numberOfViewableScreeners,
      totalRecords,
      maxResultCount,
    });
  }, [
    searchRestrictions,
    systemRestrictions,
    totalRecords,
    uac?.numberOfViewableScreeners,
  ]);

  const isPlanRestriction = useMemo(
    () => checkIsPlanRestriction(searchRestrictions),
    [searchRestrictions]
  );

  if (isFeatureEnabled) {
    return {
      companies: companiesGQL,
      fetchNextPage: enhancedFetchNextPageGQL,
      hasNextPage: hasNextPageGQL,
      isFetching: isFetchingCompaniesGQL,
      isFetched: isFetchedGQL,
      totalCompanies: totalCompaniesGQL,
      newCompaniesCount: firstPage?.newCompaniesTotalHits,
      isRestricted,
      restrictedReason,
      isPlanRestriction,
    };
  }

  return {
    companies: companiesRestApi,
    fetchNextPage: fetchNextPageRestApi,
    hasNextPage: hasNextPageRestApi,
    isFetching: isFetchingCompaniesRestApi,
    isFetched: isFetchedRestApi,
    totalCompanies: totalCompaniesRestApi,
    newCompaniesCount: null,
    isRestricted: null,
    restrictedReason: null,
    isPlanRestriction: false,
  };
};
